import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useCartStore, useAuthStore } from "@/lib/stores";
import { Check, Gift, CreditCard, ArrowLeft, ShieldCheck, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import BrandLogo from "@/components/BrandLogo";
import SEO from "@/components/SEO";

export default function Checkout() {
    const nav = useNavigate();
    const { items, totalPayable, totalSavings, clear } = useCartStore();
    const { user } = useAuthStore();
    const [step, setStep] = useState(1);
    const [isGift, setIsGift] = useState(false);
    const [gift, setGift] = useState({
        recipient_name: "",
        recipient_phone: "",
        recipient_email: "",
        personal_message: "",
        delivery_channel: "whatsapp",
    });
    const [buyerEmail, setBuyerEmail] = useState(user?.email || "");
    const [creating, setCreating] = useState(false);
    const [order, setOrder] = useState(null);

    useEffect(() => {
        if (!user) nav("/");
        if (items.length === 0) nav("/catalog");
    }, [user, items, nav]);

    const next = () => setStep((s) => Math.min(3, s + 1));
    const back = () => setStep((s) => Math.max(1, s - 1));

    const createAndPay = async () => {
        if (isGift && !gift.recipient_name) { toast.error("Recipient name is required"); return; }
        if (isGift && gift.delivery_channel !== "email" && gift.recipient_phone.length < 10) { toast.error("Recipient phone is required"); return; }
        setCreating(true);
        try {
            const payload = {
                items: items.map((i) => ({
                    brand_id: i.brand_id, sku: i.sku, brand_name: i.brand_name,
                    denomination: i.denomination, quantity: i.quantity,
                })),
                is_gift: isGift,
                gift: isGift ? gift : null,
                buyer_email: buyerEmail || undefined,
            };
            const r = await api.post("/orders/create", payload);
            setOrder(r.data);
            launchRazorpay(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to create order");
        } finally {
            setCreating(false);
        }
    };

    const verify = async (order_id, rzp) => {
        try {
            const r = await api.post("/orders/verify", {
                order_id,
                razorpay_order_id: rzp.razorpay_order_id,
                razorpay_payment_id: rzp.razorpay_payment_id,
                razorpay_signature: rzp.razorpay_signature,
            });
            clear();
            nav(`/orders/${order_id}/success`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Payment verification failed");
        }
    };

    const launchRazorpay = (orderData) => {
        const rzp = new window.Razorpay({
            key: orderData.razorpay_key_id,
            amount: orderData.amount,
            currency: "INR",
            name: "LoyltyKart",
            description: `${items.length} gift card(s)`,
            order_id: orderData.razorpay_order_id,
            handler: (res) => verify(orderData.order_id, res),
            prefill: {
                name: user?.name || "",
                email: buyerEmail || "",
                contact: user?.phone || "",
            },
            theme: { color: "#0B1437" },
        });
        rzp.open();
    };

    return (
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12 py-8 md:py-12">
            <SEO title="Checkout" description="Secure checkout on LoyltyKart." path="/checkout" noindex />
            <button onClick={() => nav(-1)} className="text-sm text-[#6C6F7A] inline-flex items-center gap-1.5 mb-4 hover:text-[#0B1437]">
                <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="font-serif text-3xl md:text-5xl mb-6">Checkout</h1>

            <div className="flex items-center gap-2 mb-8">
                {[
                    { n: 1, t: "Gift Option" },
                    { n: 2, t: "Review" },
                    { n: 3, t: "Pay" },
                ].map((s, i) => (
                    <div key={s.n} className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                            step >= s.n ? "bg-[#0B1437] text-white" : "bg-[#EFECE2] text-[#6C6F7A]"
                        }`}>
                            {step > s.n ? <Check className="w-4 h-4" /> : s.n}
                        </div>
                        <span className={`text-sm ${step === s.n ? "font-semibold text-[#0B1437]" : "text-[#6C6F7A]"}`}>
                            {s.t}
                        </span>
                        {i < 2 && <div className={`w-8 md:w-16 h-px ${step > s.n ? "bg-[#0B1437]" : "bg-[#E5E0D5]"}`} />}
                    </div>
                ))}
            </div>

            <div className="grid md:grid-cols-12 gap-8">
                <div className="md:col-span-7">
                    {step === 1 && (
                        <div className="bg-white border border-[#E5E0D5] rounded-2xl p-6 md:p-8 space-y-5">
                            <h2 className="font-serif text-2xl">Who is this for?</h2>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    data-testid="self-btn"
                                    onClick={() => setIsGift(false)}
                                    className={`p-5 rounded-2xl border-2 text-left transition-all ${!isGift ? "border-[#0B1437] bg-[#0B1437]/5" : "border-[#E5E0D5]"}`}
                                >
                                    <Sparkles className="w-6 h-6 text-[#0B1437] mb-2" />
                                    <div className="font-semibold">For myself</div>
                                    <div className="text-xs text-[#6C6F7A] mt-1">Delivered to your account & email</div>
                                </button>
                                <button
                                    data-testid="gift-btn"
                                    onClick={() => setIsGift(true)}
                                    className={`p-5 rounded-2xl border-2 text-left transition-all ${isGift ? "border-[#0B1437] bg-[#0B1437]/5" : "border-[#E5E0D5]"}`}
                                >
                                    <Gift className="w-6 h-6 text-[#0B1437] mb-2" />
                                    <div className="font-semibold">Gift to someone</div>
                                    <div className="text-xs text-[#6C6F7A] mt-1">Sent via WhatsApp or email</div>
                                </button>
                            </div>

                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-[#6C6F7A]">Your email (for receipt)</label>
                                <input
                                    data-testid="buyer-email-input"
                                    value={buyerEmail}
                                    onChange={(e) => setBuyerEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="mt-2 w-full px-4 py-3 border border-[#E5E0D5] rounded-xl text-sm outline-none focus:border-[#0B1437]"
                                />
                            </div>

                            {isGift && (
                                <div className="space-y-3 pt-2 border-t border-[#E5E0D5]">
                                    <h3 className="font-serif text-lg">Recipient details</h3>
                                    <div className="grid md:grid-cols-2 gap-3">
                                        <input
                                            data-testid="recipient-name-input"
                                            placeholder="Recipient name"
                                            value={gift.recipient_name}
                                            onChange={(e) => setGift({ ...gift, recipient_name: e.target.value })}
                                            className="px-4 py-3 border border-[#E5E0D5] rounded-xl text-sm outline-none focus:border-[#0B1437]"
                                        />
                                        <input
                                            data-testid="recipient-phone-input"
                                            placeholder="Recipient phone (+91)"
                                            value={gift.recipient_phone}
                                            onChange={(e) => setGift({ ...gift, recipient_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                                            className="px-4 py-3 border border-[#E5E0D5] rounded-xl text-sm outline-none focus:border-[#0B1437]"
                                        />
                                    </div>
                                    <input
                                        data-testid="recipient-email-input"
                                        placeholder="Recipient email (optional)"
                                        value={gift.recipient_email}
                                        onChange={(e) => setGift({ ...gift, recipient_email: e.target.value })}
                                        className="w-full px-4 py-3 border border-[#E5E0D5] rounded-xl text-sm outline-none focus:border-[#0B1437]"
                                    />
                                    <textarea
                                        data-testid="gift-message-input"
                                        placeholder="Personal message (optional)"
                                        rows={3}
                                        value={gift.personal_message}
                                        onChange={(e) => setGift({ ...gift, personal_message: e.target.value })}
                                        className="w-full px-4 py-3 border border-[#E5E0D5] rounded-xl text-sm outline-none focus:border-[#0B1437] resize-none"
                                    />
                                    <div>
                                        <label className="text-xs uppercase tracking-[0.2em] text-[#6C6F7A]">Delivery channel</label>
                                        <div className="flex gap-2 mt-2">
                                            {["whatsapp", "email", "both"].map((c) => (
                                                <button
                                                    key={c}
                                                    data-testid={`channel-${c}`}
                                                    onClick={() => setGift({ ...gift, delivery_channel: c })}
                                                    className={`px-4 py-2 rounded-full text-sm border-2 capitalize ${
                                                        gift.delivery_channel === c
                                                            ? "border-[#0B1437] bg-[#0B1437] text-white"
                                                            : "border-[#E5E0D5]"
                                                    }`}
                                                >
                                                    {c}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <button data-testid="next-step-btn" onClick={next} className="rk-btn-primary w-full md:w-auto !px-10">Continue</button>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="bg-white border border-[#E5E0D5] rounded-2xl p-6 md:p-8 space-y-4">
                            <h2 className="font-serif text-2xl">Review your order</h2>
                            <div className="space-y-3">
                                {items.map((it) => (
                                    <div key={it.key} className="flex gap-3 py-3 border-b border-[#E5E0D5] last:border-0">
                                        <div className="w-14 h-14 rk-logo-box flex items-center justify-center shrink-0">
                                            <BrandLogo brand={it} size="sm" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold text-sm">{it.brand_name}</p>
                                            <p className="text-xs text-[#6C6F7A]">{formatINR(it.denomination)} × {it.quantity}</p>
                                        </div>
                                        <div className="text-sm font-semibold">
                                            {formatINR(it.denomination * it.quantity * (1 - (it.discount_percentage || 0) / 100))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {isGift && (
                                <div className="bg-[#EFECE2] rounded-xl p-4 text-sm">
                                    <div className="font-semibold text-[#0B1437]">
                                        🎁 Gift for {gift.recipient_name}
                                    </div>
                                    <div className="text-[#6C6F7A] mt-1">
                                        {gift.delivery_channel === "whatsapp" && `via WhatsApp +91 ${gift.recipient_phone}`}
                                        {gift.delivery_channel === "email" && `via email to ${gift.recipient_email}`}
                                        {gift.delivery_channel === "both" && `via WhatsApp & email`}
                                    </div>
                                    {gift.personal_message && (
                                        <p className="italic text-[#6C6F7A] mt-2">"{gift.personal_message}"</p>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-3">
                                <button onClick={back} className="rk-btn-ghost">Back</button>
                                <button data-testid="go-to-pay-btn" onClick={next} className="rk-btn-primary flex-1">Continue to Pay</button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="bg-white border border-[#E5E0D5] rounded-2xl p-6 md:p-8 space-y-5">
                            <h2 className="font-serif text-2xl">Payment</h2>
                            <div className="bg-[#0B1437] text-white rounded-2xl p-5">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] opacity-80 mb-1">
                                    <ShieldCheck className="w-3.5 h-3.5" /> Secured by Razorpay
                                </div>
                                <div className="font-serif text-2xl">UPI · Cards · Netbanking · Wallets</div>
                                <p className="text-xs opacity-80 mt-1">
                                    You'll be redirected to Razorpay's secure checkout to complete your payment.
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button onClick={back} className="rk-btn-ghost">Back</button>
                                <button
                                    data-testid="pay-btn"
                                    onClick={createAndPay}
                                    disabled={creating}
                                    className="rk-btn-primary flex-1 inline-flex items-center justify-center gap-2"
                                >
                                    <CreditCard className="w-4 h-4" />
                                    {creating ? "Processing…" : `Pay ${formatINR(totalPayable())}`}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Order Summary */}
                <aside className="md:col-span-5">
                    <div className="bg-white border border-[#E5E0D5] rounded-2xl p-6 md:sticky md:top-24">
                        <h3 className="font-serif text-xl mb-4">Order summary</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-[#6C6F7A]">
                                <span>Subtotal ({items.reduce((s, i) => s + i.quantity, 0)} items)</span>
                                <span className="line-through">{formatINR(items.reduce((s, i) => s + i.denomination * i.quantity, 0))}</span>
                            </div>
                            {totalSavings() > 0 && (
                                <div className="flex justify-between text-[#1D5C42] font-semibold">
                                    <span>Instant discount</span>
                                    <span>− {formatINR(totalSavings())}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-[#6C6F7A]">
                                <span>Delivery</span>
                                <span className="text-[#1D5C42] font-semibold">FREE</span>
                            </div>
                            <div className="border-t border-[#E5E0D5] my-3" />
                            <div className="flex justify-between items-baseline">
                                <span className="font-serif text-lg">You pay</span>
                                <span data-testid="summary-total" className="font-serif text-3xl font-bold text-[#0B1437]">
                                    {formatINR(totalPayable())}
                                </span>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}
